// business.reducer.ts
import { createFeature, createReducer, on } from '@ngrx/store'
import {
  loadBusinesses,
  loadBusinessesSuccess,
  loadBusinessesFailure,
  blockBusiness,
  unblockBusiness,
  blockBusinessSuccess,
  unblockBusinessSuccess,
  blockBusinessFailure,
  unblockBusinessFailure,
  deleteBusiness,
  deleteBusinessFailure,
  deleteBusinessSuccess,
  createBusiness,
  createBusinessSuccess,
  createBusinessFailure,
  loadBusinessByIdSuccess,
  loadBusinessByIdFailure,
  updateBusiness,
  updateBusinessSuccess,
  updateBusinessFailure,
  assignOwner,
  assignOwnerSuccess,
  assignOwnerFailure,
  resetBusinessState
} from './business.actions'
import { BusinessState } from './business.state'

export const initialState: BusinessState = {
  totalCount: 0,
  businesses: [],
  loading: false,
  error: null,
  activeCount: 0,
  inactiveCount: 0,
  totalOffices: 0,
  totalDepartments: 0,
  totalDesignations: 0,
  totalRowCount: 0,
  modalBusinesses: []
}

// Create the business reducer
export const businessReducer = createReducer(
  initialState,
  on(loadBusinesses, state => ({
    ...state,
    loading: true,
    error: null
  })),
  on(
    loadBusinessesSuccess,
    (
      state,
      {
        totalCount,
        businesses,
        activeCount,
        inactiveCount,
        totalOffices,
        totalDepartments,
        totalDesignations,
        totalRowCount,
        context
      }
    ) => ({
      ...state,
      totalCount,
      activeCount,
      inactiveCount,
      businesses,
      loading: false,
      error: null,
      totalOffices,
      totalDepartments,
      totalDesignations,
      totalRowCount,
      // 👇 modal data stored separately
      modalBusinesses: context === 'list' ? businesses : state.modalBusinesses
    })
  ),
  on(loadBusinessesFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error
  })),
  on(blockBusiness, (state, { id }) => ({
    ...state,
    businesses: state.businesses.map(business =>
      String(business.id) === id ? { ...business, disabled: true } : business
    )
  })),
  on(unblockBusiness, (state, { id }) => ({
    ...state,
    businesses: state.businesses.map(business =>
      String(business.id) === id ? { ...business, disabled: false } : business
    )
  })),
  on(blockBusinessSuccess, (state, { business }) => {
    // console.log('reducer called')
    // Update the business state to reflect that it is blocked
    const updatedState = {
      ...state,
      businesses: state.businesses.map(
        b => {
          if (b.id === business.id) {
            // console.log('updated business', { ...b, ...business })
            return { ...b, ...business }
          } else return b
        } // Update the business object
      ),
      error: null
    }
    return updatedState
  }),
  on(unblockBusinessSuccess, (state, { id }) => {
    // Update the business state to reflect that it is unblocked
    return {
      ...state,
      businesses: state.businesses.map(business =>
        String(business.id) === id ? { ...business, disabled: false } : business
      ),
      error: null
    }
  }),
  on(blockBusinessFailure, (state, { error }) => ({
    ...state,
    error
  })),
  on(unblockBusinessFailure, (state, { error }) => ({
    ...state,
    error
  })),
  on(deleteBusiness, (state, { id }) => ({
    ...state,
    businesses: state.businesses.map(business =>
      String(business.id) === id ? { ...business, disabled: true } : business
    )
  })),
  on(deleteBusinessSuccess, (state, { id }) => {
    // Update the business state to reflect that it is successfully deleted
    const updatedState = {
      ...state,
      businesses: state.businesses.filter(b => b.id !== id), // Remove the business from the list
      error: null
    }
    return updatedState
  }),
  on(deleteBusinessFailure, (state, { error }) => ({
    ...state,
    error
  })),
  on(createBusiness, state => ({ ...state, loading: true })),
  on(createBusinessSuccess, (state, { business }) => ({
    ...state,
    businesses: [...state.businesses, business],
    loading: false
  })),
  on(createBusinessFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error
  })),
  on(loadBusinessByIdSuccess, (state, { business }) => ({
    ...state,
    business,
    error: null
  })),
  on(loadBusinessByIdFailure, (state, { error }) => ({
    ...state,
    business: null,
    error
  })),
  on(updateBusiness, state => ({ ...state, loading: true })),
  on(updateBusinessSuccess, (state, { business }) => ({
    ...state,
    business,
    loading: false
  })),
  on(updateBusinessFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error
  })),
  on(assignOwner, state => ({ ...state, loading: true })),
  on(assignOwnerSuccess, (state, { business }) => ({
    ...state,
    business,
    loading: false
  })),
  on(assignOwnerFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error
  })),
  on(resetBusinessState, () => initialState)
)

// Create the feature for the business state
export const businessFeature = createFeature({
  name: 'business', // Feature name
  reducer: businessReducer
})
export type { BusinessState }
// Export the feature's selectors
export const {
  name,
  reducer,
  selectBusinesses,
  selectLoading,
  selectError,
  selectTotalDepartments,
  selectTotalDesignations,
  selectTotalOffices,
  selectTotalRowCount
} = businessFeature
